import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { CircularProgress } from "@mui/joy";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { StarFilledRounded } from "@mui-symbols-material/w300";
import WidgetHeader from "../WidgetHeader";
import NodeShortcutItem from "../NodeShortcutItem";
import { StarredInstalledBaseNodeList } from "../../graphql/queries/starredInstalledBaseNodeList";
import { isNodeInTrash } from "../../utils/installedBasePath";
import _ from "lodash";

const PAGE_SIZE = 25;

interface AssetShortcutsWidgetProps {
    onDelete: () => void;
    onEdit: () => void;
}

const AssetShortcutsWidget: React.FC<AssetShortcutsWidgetProps> = ({ onDelete, onEdit }) => {
    const { t } = useTranslation();
    const [loadingMore, setLoadingMore] = useState(false);

    const { data, loading, fetchMore } = useQuery(StarredInstalledBaseNodeList, {
        variables: {
            pagination: { start: 0, limit: PAGE_SIZE },
            imageCrop: "fit",
            imageWidth: 60,
            imageHeight: 60,
        },
        fetchPolicy: 'cache-and-network',
    });

    const nodes = _.get(data, "starredInstalledBaseNodeList.installedBaseNodes", []);
    const total: number = _.get(data, "starredInstalledBaseNodeList.total", 0);
    const hasMore = nodes.length < total;
    // Starred nodes that were moved to the trash stay out of the quick access;
    // pagination math (hasMore, start) stays on the unfiltered list.
    const visibleNodes = nodes.filter((node: any) => node && !isNodeInTrash(node));

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (!hasMore || loadingMore || scrollHeight - scrollTop - clientHeight > 60) return;

        setLoadingMore(true);
        fetchMore({
            variables: {
                pagination: { start: nodes.length, limit: PAGE_SIZE },
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;
                return {
                    starredInstalledBaseNodeList: {
                        ...fetchMoreResult.starredInstalledBaseNodeList,
                        installedBaseNodes: [
                            ..._.get(prev, "starredInstalledBaseNodeList.installedBaseNodes", []),
                            ..._.get(fetchMoreResult, "starredInstalledBaseNodeList.installedBaseNodes", []),
                        ],
                    },
                };
            },
        }).finally(() => setLoadingMore(false));
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
            }}
        >
            <WidgetHeader
                title={
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <StarFilledRounded sx={{ fontSize: 18, color: "#666" }} />
                        {t("Favorites")}
                    </Box>
                }
                onDelete={onDelete}
                onEdit={onEdit}
            />

            {loading && nodes.length === 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: 1,
                    }}
                >
                    <CircularProgress size="sm" />
                </Box>
            ) : visibleNodes.length === 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: 1,
                        color: "text.secondary",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {t("No favorites yet")}
                    </Typography>
                </Box>
            ) : (
                <Box
                    className="no-drag"
                    onScroll={handleScroll}
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                    }}
                >
                    {visibleNodes.map((node: any) => (
                        <NodeShortcutItem key={node.id} node={node} />
                    ))}
                    {loadingMore && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                            <CircularProgress size="sm" />
                        </Box>
                    )}
                </Box>
            )}
        </div>
    );
};

export default AssetShortcutsWidget;
